/**
 * Employee Conversion Wizard
 * 
 * Flagship workflow demonstration of Aligned.
 * Converts an applicant to a fully onboarded employee without duplicate entry.
 * 
 * Shows progress animation as each step completes:
 * ✓ Employee Profile Created
 * ✓ Records Center Created
 * ✓ Timeline Created
 * ✓ Communication Thread Created
 * ✓ Compliance Profile Created
 * ✓ Training Queue Created
 * ✓ Guardian Registration Task Created
 * ✓ Employee Workspace Activated
 */
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, CheckCircle, Loader2, FolderOpen, Clock, MessageSquare,
  Shield, FileText, GraduationCap, UserCheck, Briefcase, AlertTriangle,
  ChevronRight, X, Sparkles
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../components/ui/sheet";
import api from "../../lib/api";

// Step icons mapping
const stepIcons = {
  validate_application: User,
  create_employee_profile: User,
  create_user_account: UserCheck,
  create_records_folder: FolderOpen,
  create_timeline: Clock,
  create_communication_thread: MessageSquare,
  create_compliance_profile: Shield,
  create_document_checklist: FileText,
  create_training_queue: GraduationCap,
  create_guardian_task: Shield,
  update_application: CheckCircle,
  activate_workspace: Briefcase,
  send_notifications: MessageSquare
};

// Step component with animation
const ConversionStep = ({ step, index, isActive, isCompleted }) => {
  const Icon = stepIcons[step.step] || CheckCircle;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ 
        opacity: 1, 
        x: 0,
        scale: isActive ? 1.02 : 1
      }}
      transition={{ delay: index * 0.15, duration: 0.3 }}
      className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
        isCompleted 
          ? "bg-green-50 border border-green-200" 
          : isActive 
            ? "bg-blue-50 border border-blue-200" 
            : "bg-secondary/30 border border-transparent"
      }`}
    >
      <div className={`p-2 rounded-full ${
        isCompleted 
          ? "bg-green-100 text-green-600" 
          : isActive 
            ? "bg-blue-100 text-blue-600"
            : "bg-secondary text-muted-foreground"
      }`}>
        {isCompleted ? (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 500 }}
          >
            <CheckCircle className="w-4 h-4" />
          </motion.div>
        ) : isActive ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Icon className="w-4 h-4" />
        )}
      </div>
      <span className={`text-sm font-medium ${
        isCompleted 
          ? "text-green-700" 
          : isActive 
            ? "text-blue-700"
            : "text-muted-foreground"
      }`}>
        {step.message}
      </span>
    </motion.div>
  );
};

// Readiness meter component
const ReadinessMeter = ({ readiness }) => {
  const score = readiness?.score || 0;
  const completed = readiness?.completed || [];
  const remaining = readiness?.remaining || [];
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Employee Readiness</span>
        <span className={`text-2xl font-bold ${
          score >= 80 ? "text-green-600" : score >= 50 ? "text-yellow-600" : "text-red-600"
        }`}>
          {score}%
        </span>
      </div>
      
      {/* Progress bar */}
      <div className="h-2 bg-secondary rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 1, delay: 0.5 }}
          className={`h-full rounded-full ${
            score >= 80 ? "bg-green-500" : score >= 50 ? "bg-yellow-500" : "bg-red-500"
          }`}
        />
      </div>
      
      {/* Completed items */}
      {completed.length > 0 && (
        <div className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">Completed</span>
          <div className="flex flex-wrap gap-1">
            {completed.map((item, i) => (
              <span key={i} className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded-full border border-green-200">
                ✓ {item.name}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Remaining items */}
      {remaining.length > 0 && (
        <div className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">Remaining ({remaining.length})</span>
          <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
            {remaining.map((item, i) => (
              <span key={i} className="text-xs px-2 py-0.5 bg-secondary text-muted-foreground rounded-full border">
                ☐ {item.name}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Estimated time */}
      {readiness?.estimated_days_to_scheduling && (
        <div className="text-xs text-muted-foreground">
          Estimated time to scheduling: <strong>{readiness.estimated_days_to_scheduling} days</strong>
        </div>
      )}
    </div>
  );
};

export default function ConversionWizard({ 
  application, 
  isOpen, 
  onClose, 
  onSuccess 
}) {
  const [stage, setStage] = useState("confirm"); // confirm, converting, complete, error
  const [steps, setSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState("");

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setStage("confirm");
      setSteps([]);
      setCurrentStepIndex(-1);
      setResult(null);
      setError(null);
      setNotes("");
    }
  }, [isOpen]);

  // Animate through steps
  useEffect(() => {
    if (stage === "converting" && steps.length > 0) {
      const timer = setInterval(() => {
        setCurrentStepIndex(prev => {
          if (prev < steps.length - 1) {
            return prev + 1;
          }
          clearInterval(timer);
          // All steps done, show complete
          setTimeout(() => setStage("complete"), 500);
          return prev;
        });
      }, 300);
      
      return () => clearInterval(timer);
    }
  }, [stage, steps]);

  const handleConvert = async () => {
    setStage("converting");
    setCurrentStepIndex(-1);
    
    try {
      const response = await api.post(
        `/hiring/applications/${application.id}/convert-to-employee`,
        { notes: notes || undefined }
      );
      
      setResult(response.data);
      setSteps(response.data.steps || []);
      
      // Start animating through steps
      if (response.data.steps?.length > 0) {
        setCurrentStepIndex(0);
      } else {
        setStage("complete");
      }
    } catch (err) {
      console.error("Conversion failed:", err);
      setError(err.response?.data?.detail || "Conversion failed. Please try again.");
      setStage("error");
    }
  };

  const handleComplete = () => {
    if (onSuccess && result) {
      onSuccess(result);
    }
    onClose();
  };

  if (!application) return null;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Convert to Employee
          </SheetTitle>
          <SheetDescription>
            {stage === "confirm" && "This will create a full employee profile with all required onboarding tasks."}
            {stage === "converting" && "Orchestrating employee onboarding..."}
            {stage === "complete" && "Employee successfully onboarded!"}
            {stage === "error" && "Conversion encountered an error."}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Confirm Stage */}
          {stage === "confirm" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Applicant Summary */}
              <div className="p-4 bg-secondary/50 rounded-lg space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold">
                      {application.first_name} {application.last_name}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {application.position_applied_for || "Caregiver"}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  <div>{application.email}</div>
                  <div>{application.phone}</div>
                </div>
              </div>

              {/* What will be created */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">The following will be created:</h4>
                <div className="grid gap-1.5 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" /> Employee Profile
                  </div>
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4" /> User Account
                  </div>
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" /> Records Center Folder
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Timeline
                  </div>
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" /> Communication Thread
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" /> Compliance Profile
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Document Checklist
                  </div>
                  <div className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" /> Training Queue
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" /> Guardian Registration Task
                  </div>
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> Employee Workspace
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about this conversion..."
                  className="w-full p-3 text-sm border rounded-lg resize-none h-20 focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleConvert} className="flex-1">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Convert to Employee
                </Button>
              </div>
            </motion.div>
          )}

          {/* Converting Stage */}
          {stage === "converting" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              <div className="text-center py-4">
                <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Orchestrating {application.first_name}&apos;s onboarding...
                </p>
              </div>

              {/* Steps with animation */}
              <div className="space-y-2">
                {steps.map((step, index) => (
                  <ConversionStep
                    key={step.step}
                    step={step}
                    index={index}
                    isActive={index === currentStepIndex}
                    isCompleted={index < currentStepIndex || (index === currentStepIndex && currentStepIndex === steps.length - 1)}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* Complete Stage */}
          {stage === "complete" && result && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              {/* Success header */}
              <div className="text-center py-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 300, delay: 0.2 }}
                  className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </motion.div>
                <h3 className="text-lg font-semibold text-green-700">
                  Conversion Complete!
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {result.employee?.first_name} {result.employee?.last_name} is now ready for onboarding.
                </p>
              </div>

              {/* Employee summary */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-green-800">Employee ID</span>
                  <span className="text-sm text-green-700 font-mono">
                    {result.employee?.id?.slice(0, 8)}...
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-green-800">Status</span>
                  <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                    Pending Guardian Registration
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-green-800">Hire Date</span>
                  <span className="text-sm text-green-700">{result.employee?.hire_date}</span>
                </div>
              </div>

              {/* Completed steps */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  {result.steps?.length} Steps Completed
                </h4>
                <div className="grid grid-cols-2 gap-1.5">
                  {result.steps?.map((step, i) => (
                    <div key={i} className="text-xs flex items-center gap-1 text-green-700">
                      <CheckCircle className="w-3 h-3" />
                      {step.message}
                    </div>
                  ))}
                </div>
              </div>

              {/* Readiness meter */}
              {result.readiness && (
                <div className="border-t pt-4">
                  <ReadinessMeter readiness={result.readiness} />
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button variant="outline" onClick={onClose} className="flex-1">
                  Close
                </Button>
                <Button onClick={handleComplete} className="flex-1">
                  View Employee
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Error Stage */}
          {stage === "error" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-red-700">
                  Conversion Failed
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {error}
                </p>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={onClose} className="flex-1">
                  Close
                </Button>
                <Button onClick={() => setStage("confirm")} className="flex-1">
                  Try Again
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
